// Shared parent-portal account management component (Aug 2026).
//
// Rendered in two places so both surfaces stay in lockstep:
//   1. Student Profile → Parent / Guardian tab (per-parent card).
//   2. Communication → Parent Portal Management "Manage" drawer.
//
// Both mount PortalAccessCard, which owns:
//   - the credential card (username / login email / masked password)
//   - the Reset Password dialog (generate / manual modes)
//   - the one-time credentials modal (only place plaintext appears)
//   - enable/disable + send-login-instructions + create-portal actions
//
// This file MUST NOT invent new endpoints — it wraps the existing
// RTK mutations only, so admin behaviour matches everywhere.
//
// Security: the parent's existing password is NEVER shown, retrieved,
// or logged. Only a freshly-set temporary password is surfaced, and
// only in the one-time modal for the immediate reset response.

import React, { useState } from "react";
import {
  Box, Typography, Button, CircularProgress, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";
import SendIcon from "@mui/icons-material/Send";
import LockResetIcon from "@mui/icons-material/LockReset";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoDisturbOnOutlinedIcon from "@mui/icons-material/DoDisturbOnOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNotifier } from "../../Core/Notifier";
import {
  useEnableParentPortalMutation,
  useResetParentPasswordMutation,
  useSendParentLoginDetailsMutation,
  useToggleParentAccessMutation,
} from "../../Api/attoDeskApi";

export interface PortalAccessCardAccount {
  has_account: boolean;
  username?: string | null;
  account_email?: string | null;
  account_status?: string | null;  // "active" | "inactive" | null
  last_login?: string | null;      // ISO string
}

interface Props {
  parentId: string;
  parentName?: string;
  contactEmail?: string;
  account?: PortalAccessCardAccount;
  /** Fires after a successful mutation so the caller can refetch. */
  onChanged?: () => void;
  /** If false, hides the contact-vs-portal-email mismatch banner. */
  showEmailMismatchBanner?: boolean;
}

const copyText = (text: string, label: string, notify: (s: string) => void) => {
  navigator.clipboard?.writeText(text).then(() => notify(`${label} copied`)).catch(() => {});
};

const CredentialRow: React.FC<{
  icon: React.ReactNode; label: string; value: React.ReactNode;
  mono?: boolean; copyValue?: string;
}> = ({ icon, label, value, mono = false, copyValue }) => {
  const { showMessage } = useNotifier();
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1.5, py: 1.25,
      borderBottom: "1px solid rgba(12,90,70,0.08)",
      "&:last-child": { borderBottom: "none" },
    }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: "10px",
        bgcolor: alpha("#0C5A46", 0.09), color: "#0C5A46",
        display: "flex", alignItems: "center", justifyContent: "center",
        "& svg": { fontSize: 18 }, flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{
          fontSize: 10.5, fontWeight: 700, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.08em",
        }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: 14, fontWeight: 700, color: "text.primary",
          fontFamily: mono ? "monospace" : undefined, letterSpacing: mono ? 2 : undefined,
          mt: 0.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {value}
        </Typography>
      </Box>
      {copyValue && (
        <Tooltip title={`Copy ${label.toLowerCase()}`}>
          <IconButton size="small" onClick={() => copyText(copyValue, label, showMessage)}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const PortalAccessCard: React.FC<Props> = ({
  parentId, parentName, contactEmail, account, onChanged, showEmailMismatchBanner = true,
}) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [enablePortal, { isLoading: enabling }]     = useEnableParentPortalMutation();
  const [resetPassword, { isLoading: resetting }]   = useResetParentPasswordMutation();
  const [sendLogin, { isLoading: sendingLogin }]    = useSendParentLoginDetailsMutation();
  const [toggleAccess, { isLoading: toggling }]     = useToggleParentAccessMutation();
  const anyRunning = enabling || resetting || sendingLogin || toggling;

  const hasAccount = !!account?.has_account;
  const isEnabled = (account?.account_status ?? "active") === "active";
  const loginUrl = `${window.location.origin}/login`;

  // One-time credentials modal.
  const [creds, setCreds] = useState<{ username: string; password: string } | null>(null);

  // Reset-password dialog.
  const [resetOpen, setResetOpen] = useState(false);
  const [resetMode, setResetMode] = useState<"generate" | "manual">("generate");
  const [resetPw, setResetPw] = useState("");
  const [resetPwConfirm, setResetPwConfirm] = useState("");
  // Aug 2026 — draft candidate for "generate" mode. The password is
  // produced locally in the browser via Web Crypto and stays here
  // until the admin clicks the final Reset Password button. Nothing
  // is written to the parent account until that final button is
  // clicked (spec §3/§5).
  const [genCandidate, setGenCandidate] = useState<string>("");

  // Create-portal-account dialog (parallel to reset). Separate state
  // slots so a candidate drafted in one dialog can never leak into
  // the other.
  const [createOpen, setCreateOpen] = useState(false);
  const [createMode, setCreateMode] = useState<"generate" | "manual">("generate");
  const [createPw, setCreatePw] = useState("");
  const [createPwConfirm, setCreatePwConfirm] = useState("");
  const [createCandidate, setCreateCandidate] = useState<string>("");
  const regenerateCreateCandidate = () => setCreateCandidate(generateSecureCandidate());
  const openCreateDialog = () => {
    setCreateMode("generate"); setCreatePw(""); setCreatePwConfirm("");
    setCreateCandidate(""); setCreateOpen(true);
  };
  const closeCreateDialog = () => {
    setCreateOpen(false); setCreatePw(""); setCreatePwConfirm(""); setCreateCandidate("");
  };
  const createManualValid = createPw.trim().length >= 8 && createPw === createPwConfirm;
  const canSubmitCreate = createMode === "generate"
    ? createCandidate.length >= 8 && !enabling
    : createManualValid && !enabling;

  /**
   * Cryptographic temporary-password generator. Runs entirely in the
   * browser via window.crypto.getRandomValues — no network call.
   * Uses an unambiguous alphabet (no 0/O/1/l/I) and post-guarantees
   * the existing min-8 + letter + digit policy passes.
   */
  const generateSecureCandidate = (): string => {
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    const len = 12;
    if (typeof window === "undefined" || !window.crypto?.getRandomValues) return "";
    const buf = new Uint32Array(len);
    window.crypto.getRandomValues(buf);
    const out: string[] = [];
    for (let i = 0; i < len; i++) out.push(alphabet[buf[i] % alphabet.length]);
    if (!/[A-Za-z]/.test(out.join(""))) out[0] = "A";
    if (!/\d/.test(out.join(""))) out[1] = "7";
    return out.join("");
  };
  const regenerateGenCandidate = () => setGenCandidate(generateSecureCandidate());

  const openResetDialog = () => {
    setResetMode("generate"); setResetPw(""); setResetPwConfirm("");
    setGenCandidate(""); setResetOpen(true);
  };
  const closeResetDialog = () => {
    setResetOpen(false); setResetPw(""); setResetPwConfirm(""); setGenCandidate("");
  };
  const manualValid = resetPw.trim().length >= 8 && resetPw === resetPwConfirm;
  // Generate mode now requires an admin-generated candidate before
  // the Reset button lights up — the previous "generate mode always
  // ready" behaviour is the bug (it made a single click hit the DB).
  const canSubmitReset = resetMode === "generate"
    ? genCandidate.length >= 8 && !resetting
    : manualValid && !resetting;

  const copyCreds = () => {
    if (!creds) return;
    copyText(`Username: ${creds.username}\nPassword: ${creds.password}\nLogin: ${loginUrl}`, "Credentials", showMessage);
  };

  const submitReset = async () => {
    try {
      // Both modes now go through the manual branch of the backend
      // reset endpoint — the difference is only WHERE the password was
      // typed. Generate mode uses the locally-drafted candidate. This
      // keeps a single auditable code path and guarantees Generate
      // cannot save until the admin explicitly clicks Reset Password.
      const chosen = resetMode === "generate" ? genCandidate : resetPw;
      const confirm = resetMode === "generate" ? genCandidate : resetPwConfirm;
      const args = {
        id: parentId,
        mode: "manual" as const,
        new_password: chosen,
        confirm_password: confirm,
      };
      const res: any = await resetPassword(args).unwrap();
      if (!res.status) {
        showErrorMessage(res.message || "Reset failed.");
        return;
      }
      showMessage(res.message || "Password reset.");
      closeResetDialog();
      if (res.portal?.temp_password) {
        setCreds({ username: res.portal.username, password: res.portal.temp_password });
      }
      onChanged?.();
    } catch {
      showErrorMessage("Something went wrong");
    }
  };

  const handleSendLogin = async () => {
    try {
      const res: any = await sendLogin(parentId).unwrap();
      if (!res?.status) { showErrorMessage(res?.message || "Failed to send."); return; }
      showMessage(res.message || "Login instructions sent.");
      onChanged?.();
    } catch { showErrorMessage("Something went wrong"); }
  };

  // Submit runs only when the admin clicks the final "Create Portal
  // Account" button inside the dialog. Both modes now go through the
  // manual branch of the backend enable-portal endpoint so create + reset
  // share the same auditable credential writer.
  const submitCreate = async () => {
    try {
      const chosen = createMode === "generate" ? createCandidate : createPw;
      const confirm = createMode === "generate" ? createCandidate : createPwConfirm;
      const args = {
        id: parentId,
        mode: "manual" as const,
        new_password: chosen,
        confirm_password: confirm,
      };
      const res: any = await enablePortal(args).unwrap();
      if (!res?.status) { showErrorMessage(res?.message || "Failed."); return; }
      showMessage(res.message || "Portal created.");
      closeCreateDialog();
      if (res.portal?.temp_password) {
        setCreds({ username: res.portal.username, password: res.portal.temp_password });
      }
      onChanged?.();
    } catch { showErrorMessage("Something went wrong"); }
  };

  const handleToggle = async () => {
    try {
      const status = isEnabled ? "disabled" : "active";
      const res: any = await toggleAccess({ parent_id: parentId, status }).unwrap();
      if (!res?.status) { showErrorMessage(res?.message || "Failed."); return; }
      showMessage(res.message || (isEnabled ? "Access disabled." : "Access enabled."));
      onChanged?.();
    } catch { showErrorMessage("Something went wrong"); }
  };

  // ── Not-Created state ──────────────────────────────────────────────
  if (!hasAccount) {
    return (
      <Box>
        <Box>
          <Typography sx={{
            fontSize: 12, fontWeight: 700, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.1em",
          }}>Portal Access</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5, mb: 2 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#9ca3af" }} />
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: "text.secondary" }}>Not Created</Typography>
          </Box>
        </Box>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1.5 }}>
          This parent doesn't have a portal login yet. Create the account to auto-generate a username and a temporary password.
        </Typography>
        <Button
          variant="contained"
          startIcon={<LockOpenIcon />}
          onClick={openCreateDialog}
          disabled={enabling}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: "#0C5A46", boxShadow: "none", "&:hover": { bgcolor: "#0a4939", boxShadow: "none" } }}
        >
          Create Portal Account
        </Button>
        {renderCreateDialog()}
        {renderCredsModal()}
      </Box>
    );
  }

  // ── Active/Disabled account ────────────────────────────────────────
  const trimmedContact = (contactEmail || "").trim().toLowerCase();
  const trimmedPortal = (account?.account_email || "").trim().toLowerCase();
  const emailsDiffer = showEmailMismatchBanner && !!trimmedPortal && !!trimmedContact && trimmedPortal !== trimmedContact;

  return (
    <Box>
      {/* Header row */}
      <Box>
        <Typography sx={{
          fontSize: 12, fontWeight: 700, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.1em",
        }}>Portal Access</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: isEnabled ? "#16a34a" : "#9ca3af" }} />
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: isEnabled ? "#0C5A46" : "text.secondary" }}>
            {isEnabled ? "Active" : "Disabled"}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.5, mb: 2 }}>
          {account?.last_login
            ? `Last login: ${dayjs(account.last_login).format("MMM D, YYYY · h:mm A")}`
            : "Never logged in"}
        </Typography>
      </Box>

      {/* Mint credential block */}
      <Box sx={{
        border: "1px solid rgba(12,90,70,0.18)",
        borderRadius: "14px",
        background: `linear-gradient(180deg, #EFF8F5 0%, #FFFFFF 100%)`,
        px: 2.25, py: 0.5, mb: 2,
      }}>
        <CredentialRow icon={<PersonOutlineIcon />} label="Login Username" value={account?.username || "—"} copyValue={account?.username || undefined} />
        <CredentialRow icon={<EmailOutlinedIcon />} label="Login Email" value={account?.account_email || "—"} copyValue={account?.account_email || undefined} />
        <CredentialRow icon={<KeyOutlinedIcon />} label="Password" value="••••••••" mono />
      </Box>

      {emailsDiffer && (
        <Box sx={{
          display: "flex", gap: 1, alignItems: "flex-start",
          p: 1.5, mb: 2, borderRadius: "10px",
          bgcolor: "#FFF8E8", border: "1px solid rgba(180,83,9,0.2)",
        }}>
          <Typography sx={{ fontSize: 12, color: "#B45309" }}>
            <b>Heads up —</b> the parent's contact email <code>{trimmedContact}</code> differs from their portal login email <code>{trimmedPortal}</code>. Reset instructions are delivered to the portal login email.
          </Typography>
        </Box>
      )}

      {/* Actions */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Button
          variant="contained" size="small"
          startIcon={sendingLogin ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
          onClick={handleSendLogin}
          disabled={anyRunning}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: "#0C5A46", boxShadow: "none", "&:hover": { bgcolor: "#0a4939", boxShadow: "none" } }}
        >
          Send Login Instructions
        </Button>
        <Button
          variant="outlined" size="small"
          startIcon={resetting ? <CircularProgress size={14} /> : <LockResetIcon />}
          onClick={openResetDialog}
          disabled={anyRunning}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", borderColor: "rgba(12,90,70,0.35)", color: "#0C5A46", "&:hover": { borderColor: "#0C5A46", bgcolor: alpha("#0C5A46", 0.04) } }}
        >
          Reset Password
        </Button>
        <Button
          variant="outlined" size="small"
          startIcon={toggling ? <CircularProgress size={14} /> : (isEnabled ? <DoDisturbOnOutlinedIcon /> : <CheckCircleOutlineIcon />)}
          onClick={handleToggle}
          disabled={anyRunning}
          sx={{
            textTransform: "none", fontWeight: 700, borderRadius: "10px",
            borderColor: isEnabled ? "rgba(185,28,28,0.35)" : "rgba(22,163,74,0.35)",
            color: isEnabled ? "#B91C1C" : "#16a34a",
            "&:hover": { bgcolor: alpha(isEnabled ? "#B91C1C" : "#16a34a", 0.04), borderColor: isEnabled ? "#B91C1C" : "#16a34a" },
          }}
        >
          {isEnabled ? "Disable Access" : "Enable Access"}
        </Button>
      </Box>

      <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 2 }}>
        <b>Send Login Instructions</b> emails the parent their username, a fresh temporary password, and a set-password link.
        {" "}<b>Reset Password</b> issues a new temporary password for you to share manually — the current password is never shown.
      </Typography>

      {/* Reset Password dialog */}
      <Dialog open={resetOpen} onClose={closeResetDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Reset Parent Portal Password</DialogTitle>
        <DialogContent dividers>
          <Box sx={{
            p: 1.75, mb: 2.5, borderRadius: "12px",
            border: "1px solid rgba(12,90,70,0.18)",
            background: `linear-gradient(180deg, #EFF8F5 0%, #FFFFFF 100%)`,
          }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.08em" }}>Parent</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 0.25 }}>{parentName || "This parent"}</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: 1 }}>
              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>Username</Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{account?.username || "—"}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>Login Email</Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{account?.account_email || "—"}</Typography>
              </Box>
            </Box>
          </Box>

          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Choose how to reset
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            {[
              { key: "generate" as const, icon: <AutoAwesomeOutlinedIcon />, title: "Generate Temporary Password", desc: "Recommended. System generates a secure temporary password to share with the parent." },
              { key: "manual"   as const, icon: <EditOutlinedIcon />,        title: "Set Password Manually",        desc: "Type a temporary password yourself. Minimum 8 characters." },
            ].map((opt) => {
              const active = resetMode === opt.key;
              return (
                <Box
                  key={opt.key}
                  onClick={() => {
                    // Mode-switch clears the other mode's draft state
                    // so a stale candidate never gets submitted (spec §11).
                    setResetMode(opt.key);
                    if (opt.key === "generate") { setResetPw(""); setResetPwConfirm(""); }
                    else { setGenCandidate(""); }
                  }}
                  sx={{
                    px: 1.75, py: 1.25, borderRadius: "12px", cursor: "pointer",
                    display: "flex", gap: 1.25, alignItems: "flex-start",
                    border: `1.5px solid ${active ? "#0C5A46" : "rgba(0,0,0,0.1)"}`,
                    bgcolor: active ? alpha("#0C5A46", 0.05) : "transparent",
                    transition: "all 150ms",
                  }}
                >
                  <Box sx={{
                    width: 20, height: 20, borderRadius: "50%",
                    border: `2px solid ${active ? "#0C5A46" : "rgba(0,0,0,0.25)"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, mt: 0.25,
                  }}>
                    {active && <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#0C5A46" }} />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Box sx={{ color: active ? "#0C5A46" : "text.secondary", "& svg": { fontSize: 17 } }}>{opt.icon}</Box>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: active ? "#0C5A46" : "text.primary" }}>{opt.title}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>{opt.desc}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {resetMode === "generate" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {!genCandidate ? (
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeOutlinedIcon />}
                  onClick={regenerateGenCandidate}
                  sx={{
                    alignSelf: "flex-start",
                    textTransform: "none", fontWeight: 700, borderRadius: "10px",
                    bgcolor: "#0C5A46", boxShadow: "none",
                    "&:hover": { bgcolor: "#0a4939", boxShadow: "none" },
                  }}
                >
                  Generate Temporary Password
                </Button>
              ) : (
                <>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Auto-Generated Temporary Password
                  </Typography>
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    p: 1.25, borderRadius: "10px",
                    bgcolor: alpha("#0C5A46", 0.06),
                    border: "1px solid rgba(12,90,70,0.22)",
                  }}>
                    <Typography sx={{
                      flex: 1, fontFamily: "monospace",
                      fontSize: 15, fontWeight: 700, color: "#0C5A46",
                      letterSpacing: "0.5px", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {genCandidate}
                    </Typography>
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={() => copyText(genCandidate, "Password", showMessage)} aria-label="Copy generated password">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box>
                    <Button
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={regenerateGenCandidate}
                      sx={{ textTransform: "none", fontWeight: 700, color: "#0C5A46" }}
                    >
                      Regenerate
                    </Button>
                  </Box>
                  <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                    This password has not been saved yet. It will only take effect after you click
                    <b> Reset Password</b>.
                  </Typography>
                </>
              )}
            </Box>
          )}

          {resetMode === "manual" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>New Temporary Password *</Typography>
                <TextField
                  fullWidth size="small" type="text" autoComplete="new-password"
                  value={resetPw}
                  onChange={(e) => setResetPw(e.target.value)}
                  placeholder="Minimum 8 characters"
                  inputProps={{ maxLength: 72 }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontFamily: "monospace", letterSpacing: 1 } }}
                />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>Confirm Password *</Typography>
                <TextField
                  fullWidth size="small" type="text" autoComplete="new-password"
                  value={resetPwConfirm}
                  onChange={(e) => setResetPwConfirm(e.target.value)}
                  placeholder="Re-enter the password"
                  inputProps={{ maxLength: 72 }}
                  error={resetPwConfirm.length > 0 && resetPw !== resetPwConfirm}
                  helperText={resetPwConfirm.length > 0 && resetPw !== resetPwConfirm ? "Passwords do not match." : " "}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontFamily: "monospace", letterSpacing: 1 } }}
                />
              </Box>
            </Box>
          )}

          <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.5 }}>
            The current password is never displayed. Only the newly set temporary password is shown once, on the next screen.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeResetDialog} sx={{ textTransform: "none", borderRadius: "10px" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitReset}
            disabled={!canSubmitReset}
            startIcon={resetting ? <CircularProgress size={14} color="inherit" /> : <LockResetIcon />}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: "#0C5A46", boxShadow: "none", "&:hover": { bgcolor: "#0a4939", boxShadow: "none" } }}
          >
            {resetting ? "Resetting…" : "Reset Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {renderCredsModal()}
    </Box>
  );

  function renderCreateDialog() {
    return (
      <Dialog open={createOpen} onClose={closeCreateDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Create Parent Portal Account</DialogTitle>
        <DialogContent dividers>
          <Box sx={{
            p: 1.75, mb: 2.5, borderRadius: "12px",
            border: "1px solid rgba(12,90,70,0.18)",
            background: `linear-gradient(180deg, #EFF8F5 0%, #FFFFFF 100%)`,
          }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.08em" }}>Parent</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 0.25 }}>{parentName || "This parent"}</Typography>
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>Login Email</Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {contactEmail || "—"}
              </Typography>
            </Box>
          </Box>

          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Temporary password method
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            {[
              { key: "generate" as const, icon: <AutoAwesomeOutlinedIcon />, title: "Auto-Generate", desc: "Recommended. Generate a secure temporary password to share with the parent." },
              { key: "manual"   as const, icon: <EditOutlinedIcon />,        title: "Create Manually", desc: "Type a temporary password yourself. Minimum 8 characters." },
            ].map((opt) => {
              const active = createMode === opt.key;
              return (
                <Box
                  key={opt.key}
                  onClick={() => {
                    setCreateMode(opt.key);
                    // Mode-switch clears the other mode's draft.
                    if (opt.key === "generate") { setCreatePw(""); setCreatePwConfirm(""); }
                    else { setCreateCandidate(""); }
                  }}
                  sx={{
                    px: 1.75, py: 1.25, borderRadius: "12px", cursor: "pointer",
                    display: "flex", gap: 1.25, alignItems: "flex-start",
                    border: `1.5px solid ${active ? "#0C5A46" : "rgba(0,0,0,0.1)"}`,
                    bgcolor: active ? alpha("#0C5A46", 0.05) : "transparent",
                    transition: "all 150ms",
                  }}
                >
                  <Box sx={{
                    width: 20, height: 20, borderRadius: "50%",
                    border: `2px solid ${active ? "#0C5A46" : "rgba(0,0,0,0.25)"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, mt: 0.25,
                  }}>
                    {active && <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#0C5A46" }} />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Box sx={{ color: active ? "#0C5A46" : "text.secondary", "& svg": { fontSize: 17 } }}>{opt.icon}</Box>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: active ? "#0C5A46" : "text.primary" }}>{opt.title}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>{opt.desc}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {createMode === "generate" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {!createCandidate ? (
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeOutlinedIcon />}
                  onClick={regenerateCreateCandidate}
                  sx={{
                    alignSelf: "flex-start",
                    textTransform: "none", fontWeight: 700, borderRadius: "10px",
                    bgcolor: "#0C5A46", boxShadow: "none",
                    "&:hover": { bgcolor: "#0a4939", boxShadow: "none" },
                  }}
                >
                  Generate Temporary Password
                </Button>
              ) : (
                <>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Auto-Generated Temporary Password
                  </Typography>
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    p: 1.25, borderRadius: "10px",
                    bgcolor: alpha("#0C5A46", 0.06),
                    border: "1px solid rgba(12,90,70,0.22)",
                  }}>
                    <Typography sx={{
                      flex: 1, fontFamily: "monospace",
                      fontSize: 15, fontWeight: 700, color: "#0C5A46",
                      letterSpacing: "0.5px", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {createCandidate}
                    </Typography>
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={() => copyText(createCandidate, "Password", showMessage)} aria-label="Copy generated password">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box>
                    <Button
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={regenerateCreateCandidate}
                      sx={{ textTransform: "none", fontWeight: 700, color: "#0C5A46" }}
                    >
                      Regenerate
                    </Button>
                  </Box>
                  <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                    This password has not been saved yet. It will only take effect after you click
                    <b> Create Portal Account</b>.
                  </Typography>
                </>
              )}
            </Box>
          )}

          {createMode === "manual" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>Temporary Password *</Typography>
                <TextField
                  fullWidth size="small" type="text" autoComplete="new-password"
                  value={createPw}
                  onChange={(e) => setCreatePw(e.target.value)}
                  placeholder="Minimum 8 characters"
                  inputProps={{ maxLength: 72 }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontFamily: "monospace", letterSpacing: 1 } }}
                />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>Confirm Temporary Password *</Typography>
                <TextField
                  fullWidth size="small" type="text" autoComplete="new-password"
                  value={createPwConfirm}
                  onChange={(e) => setCreatePwConfirm(e.target.value)}
                  placeholder="Re-enter the password"
                  inputProps={{ maxLength: 72 }}
                  error={createPwConfirm.length > 0 && createPw !== createPwConfirm}
                  helperText={createPwConfirm.length > 0 && createPw !== createPwConfirm ? "Passwords do not match." : " "}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontFamily: "monospace", letterSpacing: 1 } }}
                />
              </Box>
            </Box>
          )}

          <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.5 }}>
            The temporary password will only be shown once on the next screen. Nothing is saved until you click Create Portal Account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeCreateDialog} sx={{ textTransform: "none", borderRadius: "10px" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitCreate}
            disabled={!canSubmitCreate}
            startIcon={enabling ? <CircularProgress size={14} color="inherit" /> : <LockOpenIcon />}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: "#0C5A46", boxShadow: "none", "&:hover": { bgcolor: "#0a4939", boxShadow: "none" } }}
          >
            {enabling ? "Creating…" : "Create Portal Account"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  function renderCredsModal() {
    return (
      <Dialog open={!!creds} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>Password Reset Successful</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
            Share this temporary password securely with the parent. It will not be shown again after this window is closed.
          </Typography>
          <Box sx={{
            border: "1px solid rgba(12,90,70,0.18)", borderRadius: "12px",
            background: `linear-gradient(180deg, #EFF8F5 0%, #FFFFFF 100%)`,
            px: 2, py: 0.5, mb: 2,
          }}>
            <CredentialRow icon={<PersonOutlineIcon />} label="Username" value={creds?.username || "—"} copyValue={creds?.username || undefined} />
            <CredentialRow icon={<KeyOutlinedIcon />} label="Temporary Password" value={creds?.password || "—"} mono copyValue={creds?.password || undefined} />
          </Box>
          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
            Parent will be required to change the password after login.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, flexWrap: "wrap", gap: 0.75 }}>
          <Button size="small" startIcon={<ContentCopyIcon />} onClick={copyCreds} sx={{ textTransform: "none" }}>
            Copy Login Details
          </Button>
          <Button
            variant="contained"
            onClick={() => setCreds(null)}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", ml: "auto", bgcolor: "#0C5A46", boxShadow: "none", "&:hover": { bgcolor: "#0a4939", boxShadow: "none" } }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
};

export default PortalAccessCard;
